<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseButton from './BaseButton.vue';

export interface ConfirmOptions {
  title?: string;
  message: string;
  type?: 'info' | 'warning' | 'danger';
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  closeOnOverlay?: boolean;
}

const visible = ref(false);
const { t } = useI18n();
const options = ref<ConfirmOptions>({
  title: '',
  message: '',
  type: 'info',
  confirmText: '',
  cancelText: '',
  showCancel: true,
  closeOnOverlay: true,
});

let resolvePromise: ((value: boolean) => void) | null = null;

const iconConfig = computed(() => {
  const configs = {
    info: {
      bgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      iconPath: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    warning: {
      bgClass: 'bg-yellow-100',
      iconClass: 'text-yellow-600',
      iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    },
    danger: {
      bgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    },
  };
  return configs[options.value.type || 'info'];
});

const confirmButtonVariant = computed(() => {
  return options.value.type === 'danger' ? 'danger' : 'primary';
});

function show(newOptions: ConfirmOptions): Promise<boolean> {
  options.value = {
    title: newOptions.title || '',
    message: newOptions.message,
    type: newOptions.type || 'info',
    confirmText: newOptions.confirmText || t('common.confirm'),
    cancelText: newOptions.cancelText || t('common.cancel'),
    showCancel: newOptions.showCancel !== false,
    closeOnOverlay: newOptions.closeOnOverlay !== false,
  };
  visible.value = true;

  return new Promise((resolve) => {
    resolvePromise = resolve;
  });
}

function handleConfirm() {
  visible.value = false;
  resolvePromise?.(true);
  resolvePromise = null;
}

function handleCancel() {
  visible.value = false;
  resolvePromise?.(false);
  resolvePromise = null;
}

function handleOverlayClick() {
  if (options.value.closeOnOverlay) {
    handleCancel();
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (!visible.value) return;

  if (e.key === 'Escape') {
    handleCancel();
  } else if (e.key === 'Enter') {
    handleConfirm();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

defineExpose({ show });
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="visible"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="options.title ? 'confirm-title' : undefined"
        aria-describedby="confirm-message"
      >
        <div
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
          @click="handleOverlayClick"
        ></div>

        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="visible"
            class="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
            @click.stop
          >
            <div class="p-6">
              <div class="flex items-start gap-4">
                <div
                  :class="[
                    'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center',
                    iconConfig.bgClass
                  ]"
                >
                  <svg
                    :class="['w-6 h-6', iconConfig.iconClass]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      :d="iconConfig.iconPath"
                    />
                  </svg>
                </div>

                <div class="flex-1 min-w-0">
                  <h3
                    v-if="options.title"
                    id="confirm-title"
                    class="text-lg font-semibold text-gray-900"
                  >
                    {{ options.title }}
                  </h3>
                  <p
                    id="confirm-message"
                    :class="[
                      'text-gray-600',
                      options.title ? 'mt-2' : 'text-base'
                    ]"
                  >
                    {{ options.message }}
                  </p>
                </div>
              </div>
            </div>

            <div class="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <BaseButton
                v-if="options.showCancel"
                variant="outline"
                @click="handleCancel"
              >
                {{ options.cancelText }}
              </BaseButton>
              <BaseButton
                :variant="confirmButtonVariant"
                @click="handleConfirm"
              >
                {{ options.confirmText }}
              </BaseButton>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
